import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import { CatalogImageOwner } from "../../../features/floorplan3d/catalog/catalogQueries";
import { getCatalogItemDisplayName } from "../../../features/floorplan3d/catalog/catalogDisplay";
import { productColorNames, productImages } from "./filterUtils";
import { floorplannerStore } from "../../../store/floorplannerStore";
import { useCurrencyRates } from "../../../hooks/useCurrencyRates";
import { formatDisplayPrice } from "../../../utils/currency";
import { ProductFavoriteButton } from "./ProductFavoriteButton";

type Props = {
  product: CatalogImageOwner;
  onInsert: (product: CatalogImageOwner) => void;
  onMoreInfo: (product: CatalogImageOwner) => void;
};

/**
 * Client-matched product card: image, brand, retailer logo, name, price,
 * colors, and an info control. Card click inserts; info does not.
 * Price uses the current floorplan display currency (catalog storage stays SEK).
 */
export const ProductLibraryCard: React.FC<Props> = observer(({
  product,
  onInsert,
  onMoreInfo,
}) => {
  const { t, i18n } = useTranslation();
  const { ratesArray, base } = useCurrencyRates();
  const images = productImages(product);
  const [hovered, setHovered] = React.useState(false);
  const displayUrl =
    hovered && images.length > 1 ? images[1] : images[0];
  const displayName = getCatalogItemDisplayName(product);
  const colorText = productColorNames(product);
  const formattedPrice = formatDisplayPrice(
    product.price,
    floorplannerStore.currency,
    ratesArray,
    base,
    i18n.language,
  );
  const priceLabel = formattedPrice ?? t("common.priceUnavailable");

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onInsert(product)}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onInsert(product);
      }}
      className="group relative flex w-full cursor-pointer flex-col overflow-hidden rounded-[10px] bg-[#F7F6F4] text-left shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
    >
      <div
        className="relative h-44 w-full overflow-hidden sm:h-48"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt=""
            className="h-full w-full object-cover object-center"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-xs text-[#999]">
            {t("productLibrary.noProductImage")}
          </div>
        )}
        <ProductFavoriteButton product={product} />
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-0.5 bg-[#F7F6F4] p-2 sm:p-2.5">
        <div className="flex shrink-0 items-center justify-between gap-2">
          <p className="truncate text-[10px] font-medium uppercase tracking-widest text-[#5a5752] sm:text-[11px]">
            {product.brand || product.supplier?.name || t("productLibrary.boniCatalog")}
          </p>
          {product.supplier?.logo && (
            <span className="flex h-5 max-w-28 shrink-0 items-center justify-end overflow-hidden">
              <img
                src={product.supplier.logo}
                alt=""
                className="block h-full w-full object-contain object-center opacity-80"
              />
            </span>
          )}
        </div>
        <p
          className="truncate text-[13px] font-medium leading-tight text-[#111] sm:text-[14px]"
          title={displayName}
        >
          {displayName}
        </p>
        <div className="mt-auto flex items-end justify-between gap-2">
          <span className="text-[15px] font-semibold text-[#0D0C0C] sm:text-[16px]">
            {priceLabel}{" "}
            {typeof product.price === "number" && (
              <span className="text-[10px] font-medium sm:text-[11px]">
                {t("common.inclVat")}
              </span>
            )}
          </span>
          <div className="flex min-w-0 max-w-[55%] shrink-0 items-center gap-0">
            {colorText ? (
              <p className="truncate text-[10px] text-[#777] sm:text-[11px]">
                {colorText}
              </p>
            ) : null}
            <button
              type="button"
              aria-label={t("productLibrary.productInfo")}
              onClick={(event) => {
                event.stopPropagation();
                onMoreInfo(product);
              }}
              className="ml-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#777] hover:bg-[#E5E3DE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
            >
              <FiInfo size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});
